import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Module-level singleton so the indicator (TechLayout) and the control
// (TechJobMap) share one watcher — never two parallel geolocation watches.
const state = { enabled: null, watchId: null, lastSent: 0, subs: new Set() };
const notify = () => state.subs.forEach((fn) => fn(state.enabled));
const authCfg = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("tech_token")}` },
});

const sendPing = async (coords, source) => {
  try {
    await axios.post(
      `${API}/api/tech/location`,
      {
        lat: coords.latitude,
        lng: coords.longitude,
        accuracy: coords.accuracy ?? null,
        heading: coords.heading ?? null,
        speed: coords.speed ?? null,
        source,
      },
      authCfg()
    );
  } catch (_) {}
};

const startWatch = () => {
  if (window.ReactNativeWebView) {
    // Native app → background GPS task handles tracking (works when the
    // app is backgrounded; browser watch below is foreground-only).
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: "LOCATION_TRACKING_START",
        token: localStorage.getItem("tech_token"),
        apiBase: API,
      })
    );
    return;
  }
  if (!navigator.geolocation || state.watchId != null) return;
  state.watchId = navigator.geolocation.watchPosition(
    (pos) => {
      const now = Date.now();
      if (now - state.lastSent >= 45000) {
        state.lastSent = now;
        sendPing(pos.coords, "web");
      }
    },
    () => {},
    { enableHighAccuracy: false, maximumAge: 30000, timeout: 20000 }
  );
};

export const stopLocationWatch = () => {
  if (window.ReactNativeWebView) {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type: "LOCATION_TRACKING_STOP" }));
  }
  if (state.watchId != null && navigator.geolocation) {
    navigator.geolocation.clearWatch(state.watchId);
    state.watchId = null;
  }
};

// Full reset on logout so the next tech session re-initializes from the server
export const resetSharingState = () => {
  stopLocationWatch();
  state.enabled = null;
  state.lastSent = 0;
  notify();
};

const initFromServer = async () => {
  if (state.enabled !== null) return;
  try {
    const res = await axios.get(`${API}/api/tech/location/status`, authCfg());
    state.enabled = !!res.data.sharing_enabled;
  } catch {
    state.enabled = false;
  }
  if (state.enabled) startWatch();
  notify();
};

export default function useTechLocationSharing() {
  const [enabled, setEnabled] = useState(state.enabled);

  useEffect(() => {
    state.subs.add(setEnabled);
    initFromServer();
    return () => state.subs.delete(setEnabled);
  }, []);

  const setSharing = async (on) => {
    await axios.post(`${API}/api/tech/location/sharing`, { enabled: on }, authCfg());
    state.enabled = on;
    if (on) startWatch();
    else stopLocationWatch();
    notify();
  };

  return { enabled: !!enabled, loaded: enabled !== null, setSharing };
}
